// ═══════════════════════════════════════════════════════════════
// SEED: Catálogo inicial de Tipos de Caso + Categorías
// ═══════════════════════════════════════════════════════════════
//
// Ejecutar desde la consola del navegador (logueado como consultor):
//   await __seedTiposCaso()           — siembra tipos + categorías
//   await __seedCategoriasCaso()       — solo categorías
//   await __seedSoloTiposCaso()        — solo tipos
//
// Idempotente: si ya existe un tipo/categoría con el mismo código, lo saltea.
//
// Estados base del motor (CASO_ESTADOS):
//   nuevo, abierto, en-investigacion, en-espera-externa,
//   escalado, en-revision, resuelto, cerrado, cerrado-sin-accion, reabierto
//
// Tipos de SLA (CASO_SLA_TIPOS):
//   primera-respuesta, resolucion, respuesta-externa, custom
// ═══════════════════════════════════════════════════════════════

import {
  getTipoCasoPorCodigo, upsertTipoCaso, CASO_ESTADOS, CASO_SLA_TIPOS,
} from '@core/lib/tiposCaso'
import { collection, getDocs, addDoc, query, where } from 'firebase/firestore'
import { db } from '@core/lib/firebase'
import { COLLECTIONS } from '@core/lib/constants'
import { forPlatformCreate } from '@core/lib/dbHelpers'
import { upsertSLA } from '@core/lib/slas'
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore'

// ───────────────────────────────────────────────────────────────
// Estado machine base — la mayoría de tipos la heredan
// ───────────────────────────────────────────────────────────────
const TRANSICIONES_ESTANDAR = {
  [CASO_ESTADOS.NUEVO]:             [CASO_ESTADOS.ABIERTO, CASO_ESTADOS.CERRADO_SIN_ACCION],
  [CASO_ESTADOS.ABIERTO]:            [CASO_ESTADOS.EN_INVESTIGACION, CASO_ESTADOS.CERRADO_SIN_ACCION],
  [CASO_ESTADOS.EN_INVESTIGACION]:   [CASO_ESTADOS.EN_ESPERA_EXTERNA, CASO_ESTADOS.ESCALADO, CASO_ESTADOS.EN_REVISION, CASO_ESTADOS.RESUELTO, CASO_ESTADOS.CERRADO_SIN_ACCION],
  [CASO_ESTADOS.EN_ESPERA_EXTERNA]:  [CASO_ESTADOS.EN_INVESTIGACION, CASO_ESTADOS.ESCALADO, CASO_ESTADOS.RESUELTO, CASO_ESTADOS.CERRADO_SIN_ACCION],
  [CASO_ESTADOS.ESCALADO]:           [CASO_ESTADOS.EN_INVESTIGACION, CASO_ESTADOS.EN_REVISION, CASO_ESTADOS.RESUELTO],
  [CASO_ESTADOS.EN_REVISION]:        [CASO_ESTADOS.RESUELTO, CASO_ESTADOS.EN_INVESTIGACION],
  [CASO_ESTADOS.RESUELTO]:           [CASO_ESTADOS.CERRADO, CASO_ESTADOS.REABIERTO],
  [CASO_ESTADOS.CERRADO]:            [CASO_ESTADOS.REABIERTO],
  [CASO_ESTADOS.CERRADO_SIN_ACCION]: [CASO_ESTADOS.REABIERTO],
  [CASO_ESTADOS.REABIERTO]:          [CASO_ESTADOS.EN_INVESTIGACION],
}

const ESTADOS_ESTANDAR = Object.values(CASO_ESTADOS)

// ───────────────────────────────────────────────────────────────
// Catálogo inicial de TIPOS DE CASO
// ───────────────────────────────────────────────────────────────
const CATALOGO_TIPOS = [
  // ─── ML — origen webhook ─────────────────────────────────────────
  {
    codigo: 'reclamo-ml',
    nombre: 'Reclamo Mercado Libre',
    descripcion: 'Reclamo abierto por un comprador en Mercado Libre. Caso sincronizado vía webhook `claims/v2`. El timeline externo lo gestiona ML; CoWork enriquece con operador asignado, comentarios internos y tareas hijas.',
    area: 'postventa',
    moduloOrigen: 'mercadolibre',
    estadosDisponibles: ESTADOS_ESTANDAR,
    estadoInicial: CASO_ESTADOS.ABIERTO,
    transiciones: TRANSICIONES_ESTANDAR,
    slaDefaults: [
      { tipo: CASO_SLA_TIPOS.PRIMERA_RESPUESTA, minutos: 240,  ventanaAlertaMin: 60 },  // 4hs
      { tipo: CASO_SLA_TIPOS.RESPUESTA_EXTERNA, minutos: 480,  ventanaAlertaMin: 60 },  // 8hs
      { tipo: CASO_SLA_TIPOS.RESOLUCION,        minutos: 5760, ventanaAlertaMin: 720 }, // 4 días
    ],
    prioridadDefault: 'alta',
    severidadDefault: 's2',
    asignacionDefault: null,
    camposObligatorios: ['titulo', 'recursosVinculados'],
    autoResponse: {
      activo: true,
      contenido: 'Hola, recibimos tu reclamo y ya estamos revisándolo. Te respondemos dentro de las próximas 8 horas hábiles.',
    },
    requiereAprobacion: false,
    orden: 10,
  },

  {
    codigo: 'devolucion-ml',
    nombre: 'Devolución Mercado Libre',
    descripcion: 'Devolución abierta por un comprador en Mercado Libre. Caso sincronizado vía webhook `returns/v2`. Incluye coordinación logística de envío de retorno + decisión de aceptar/rechazar.',
    area: 'postventa',
    moduloOrigen: 'mercadolibre',
    estadosDisponibles: ESTADOS_ESTANDAR,
    estadoInicial: CASO_ESTADOS.ABIERTO,
    transiciones: TRANSICIONES_ESTANDAR,
    slaDefaults: [
      { tipo: CASO_SLA_TIPOS.PRIMERA_RESPUESTA, minutos: 240,  ventanaAlertaMin: 60 },
      { tipo: CASO_SLA_TIPOS.RESOLUCION,        minutos: 4320, ventanaAlertaMin: 480 }, // 3 días
    ],
    prioridadDefault: 'alta',
    severidadDefault: 's3',
    asignacionDefault: null,
    camposObligatorios: ['titulo', 'recursosVinculados'],
    autoResponse: {
      activo: true,
      contenido: 'Hola, recibimos tu solicitud de devolución. Te respondemos a la brevedad con los pasos a seguir.',
    },
    requiereAprobacion: false,
    orden: 20,
  },

  // ─── Auditoría de patrón (cluster cross-items) ───────────────────
  {
    codigo: 'auditoria-patron',
    nombre: 'Auditoría de patrón sistémico',
    descripcion: 'Investigación cross-items detectada por motor de evaluación (varios items con mismo motivo de devolución/reclamo). El operador analiza causa raíz upstream (proveedor, logística, categoría, embalaje) y documenta conclusión. Puede generar N tareas hijas concretas al cierre.',
    area: 'postventa',
    moduloOrigen: 'casos',
    estadosDisponibles: [
      CASO_ESTADOS.NUEVO, CASO_ESTADOS.ABIERTO, CASO_ESTADOS.EN_INVESTIGACION,
      CASO_ESTADOS.EN_REVISION, CASO_ESTADOS.RESUELTO, CASO_ESTADOS.CERRADO,
      CASO_ESTADOS.CERRADO_SIN_ACCION,
    ],
    estadoInicial: CASO_ESTADOS.ABIERTO,
    transiciones: {
      [CASO_ESTADOS.NUEVO]:              [CASO_ESTADOS.ABIERTO, CASO_ESTADOS.CERRADO_SIN_ACCION],
      [CASO_ESTADOS.ABIERTO]:             [CASO_ESTADOS.EN_INVESTIGACION, CASO_ESTADOS.CERRADO_SIN_ACCION],
      [CASO_ESTADOS.EN_INVESTIGACION]:    [CASO_ESTADOS.EN_REVISION, CASO_ESTADOS.RESUELTO, CASO_ESTADOS.CERRADO_SIN_ACCION],
      [CASO_ESTADOS.EN_REVISION]:         [CASO_ESTADOS.RESUELTO, CASO_ESTADOS.EN_INVESTIGACION],
      [CASO_ESTADOS.RESUELTO]:            [CASO_ESTADOS.CERRADO],
      [CASO_ESTADOS.CERRADO]:             [],
      [CASO_ESTADOS.CERRADO_SIN_ACCION]:  [],
    },
    slaDefaults: [
      { tipo: CASO_SLA_TIPOS.RESOLUCION, minutos: 7200, ventanaAlertaMin: 1440 }, // 5 días
    ],
    prioridadDefault: 'normal',
    severidadDefault: 's3',
    asignacionDefault: null,
    camposObligatorios: ['titulo', 'descripcion'],
    autoResponse: null,
    requiereAprobacion: false,
    orden: 30,
  },

  // ─── Postventa — evaluación de causa (transportista / proveedor / embalaje) ───
  // Se abren MANUAL desde la tarea que los sugiere o desde la celda del problema
  // en el dashboard de Postventa. Reincidencia: si el mismo transportista /
  // proveedor / producto ya tiene un caso, se asocia y se avisa (abierto o cerrado).
  {
    codigo: 'evaluar-transportista',
    nombre: 'Evaluar transportista',
    descripcion: 'Incidencias de entrega recurrentes con un transportista (zona): evaluar si es el problema y decidir cambio. Abierto desde Postventa.',
    area: 'postventa',
    moduloOrigen: 'casos',
    estadosDisponibles: ESTADOS_ESTANDAR,
    estadoInicial: CASO_ESTADOS.ABIERTO,
    transiciones: TRANSICIONES_ESTANDAR,
    slaDefaults: [
      { tipo: CASO_SLA_TIPOS.RESOLUCION, minutos: 7200, ventanaAlertaMin: 1440 }, // 5 días
    ],
    prioridadDefault: 'alta',
    severidadDefault: 's2',
    asignacionDefault: null,
    camposObligatorios: ['titulo', 'descripcion'],
    autoResponse: null,
    requiereAprobacion: false,
    orden: 31,
  },
  {
    codigo: 'evaluar-proveedor',
    nombre: 'Evaluar proveedor',
    descripcion: 'Fallas de calidad recurrentes de un proveedor (devoluciones por defecto): evaluar el proveedor y decidir acción. Abierto desde Postventa.',
    area: 'postventa',
    moduloOrigen: 'casos',
    estadosDisponibles: ESTADOS_ESTANDAR,
    estadoInicial: CASO_ESTADOS.ABIERTO,
    transiciones: TRANSICIONES_ESTANDAR,
    slaDefaults: [
      { tipo: CASO_SLA_TIPOS.RESOLUCION, minutos: 7200, ventanaAlertaMin: 1440 }, // 5 días
    ],
    prioridadDefault: 'alta',
    severidadDefault: 's2',
    asignacionDefault: null,
    camposObligatorios: ['titulo', 'descripcion'],
    autoResponse: null,
    requiereAprobacion: false,
    orden: 32,
  },
  {
    codigo: 'evaluar-embalaje',
    nombre: 'Evaluar embalaje',
    descripcion: 'Roturas/daños recurrentes en el envío de un producto: evaluar el embalaje y decidir cambio. Abierto desde Postventa.',
    area: 'postventa',
    moduloOrigen: 'casos',
    estadosDisponibles: ESTADOS_ESTANDAR,
    estadoInicial: CASO_ESTADOS.ABIERTO,
    transiciones: TRANSICIONES_ESTANDAR,
    slaDefaults: [
      { tipo: CASO_SLA_TIPOS.RESOLUCION, minutos: 7200, ventanaAlertaMin: 1440 }, // 5 días
    ],
    prioridadDefault: 'normal',
    severidadDefault: 's3',
    asignacionDefault: null,
    camposObligatorios: ['titulo', 'descripcion'],
    autoResponse: null,
    requiereAprobacion: false,
    orden: 33,
  },

  // ─── Consultas manuales ──────────────────────────────────────────
  {
    codigo: 'consulta-cliente',
    nombre: 'Consulta de cliente',
    descripcion: 'Caso manual abierto por una consulta/inquietud de un comprador o cliente externo que NO viene de un canal automatizado (ML, TN). Útil para gestionar consultas por email, WhatsApp directo, o que el cliente abrió desde el portal externo.',
    area: 'atencion',
    moduloOrigen: 'casos',
    estadosDisponibles: ESTADOS_ESTANDAR,
    estadoInicial: CASO_ESTADOS.ABIERTO,
    transiciones: TRANSICIONES_ESTANDAR,
    slaDefaults: [
      { tipo: CASO_SLA_TIPOS.PRIMERA_RESPUESTA, minutos: 240,  ventanaAlertaMin: 60 },  // 4hs
      { tipo: CASO_SLA_TIPOS.RESOLUCION,        minutos: 2880, ventanaAlertaMin: 480 }, // 2 días
    ],
    prioridadDefault: 'normal',
    severidadDefault: 's3',
    asignacionDefault: null,
    camposObligatorios: ['titulo'],
    autoResponse: {
      activo: true,
      contenido: 'Hola, recibimos tu consulta. Te respondemos dentro de las próximas 4 horas hábiles.',
    },
    requiereAprobacion: false,
    orden: 40,
  },

  {
    codigo: 'consulta-proveedor',
    nombre: 'Consulta a proveedor',
    descripcion: 'Caso abierto para consultar/coordinar con un proveedor externo (fabricante, importador, logística). Ej: pedido de actualización de catálogo, consulta sobre stock, coordinación de envío.',
    area: 'operaciones',
    moduloOrigen: 'casos',
    estadosDisponibles: ESTADOS_ESTANDAR,
    estadoInicial: CASO_ESTADOS.ABIERTO,
    transiciones: TRANSICIONES_ESTANDAR,
    slaDefaults: [
      { tipo: CASO_SLA_TIPOS.PRIMERA_RESPUESTA, minutos: 480,  ventanaAlertaMin: 120 }, // 8hs
      { tipo: CASO_SLA_TIPOS.RESOLUCION,        minutos: 4320, ventanaAlertaMin: 720 }, // 3 días
    ],
    prioridadDefault: 'normal',
    severidadDefault: 's3',
    asignacionDefault: null,
    camposObligatorios: ['titulo'],
    autoResponse: null,
    requiereAprobacion: false,
    orden: 50,
  },

  // ─── Incidente técnico ──────────────────────────────────────────
  {
    codigo: 'incidente-tecnico',
    nombre: 'Incidente técnico',
    descripcion: 'Caso para reportar/seguir un incidente técnico (sync fallando, error sostenido, bug operativo). Útil cuando el problema requiere investigación multi-paso y no se resuelve con una tarea simple.',
    area: 'plataforma',
    moduloOrigen: 'casos',
    estadosDisponibles: ESTADOS_ESTANDAR,
    estadoInicial: CASO_ESTADOS.ABIERTO,
    transiciones: TRANSICIONES_ESTANDAR,
    slaDefaults: [
      { tipo: CASO_SLA_TIPOS.PRIMERA_RESPUESTA, minutos: 120,  ventanaAlertaMin: 30 },  // 2hs
      { tipo: CASO_SLA_TIPOS.RESOLUCION,        minutos: 1440, ventanaAlertaMin: 240 }, // 1 día
    ],
    prioridadDefault: 'alta',
    severidadDefault: 's2',
    asignacionDefault: null,
    camposObligatorios: ['titulo', 'descripcion'],
    autoResponse: null,
    requiereAprobacion: false,
    orden: 60,
  },

  // ─── Manual genérico (catch-all) ─────────────────────────────────
  {
    codigo: 'manual',
    nombre: 'Caso manual',
    descripcion: 'Caso genérico creado manualmente sin tipo específico. Útil cuando ninguno de los otros tipos calza. Sin SLA por defecto; el operador puede agregarlos al crear.',
    area: null,
    moduloOrigen: 'casos',
    estadosDisponibles: ESTADOS_ESTANDAR,
    estadoInicial: CASO_ESTADOS.ABIERTO,
    transiciones: TRANSICIONES_ESTANDAR,
    slaDefaults: [],
    prioridadDefault: 'normal',
    severidadDefault: 's4',
    asignacionDefault: null,
    camposObligatorios: ['titulo'],
    autoResponse: null,
    requiereAprobacion: false,
    orden: 100,
  },
]

// ───────────────────────────────────────────────────────────────
// Catálogo inicial de CATEGORÍAS DE CASO (taxonomía cerrada)
// ───────────────────────────────────────────────────────────────
const CATALOGO_CATEGORIAS = [
  { codigo: 'logistica',    nombre: 'Logística',         descripcion: 'Problemas o gestiones sobre envíos, embalaje, transportistas, zonas, tiempos.', orden: 10 },
  { codigo: 'producto',     nombre: 'Producto',          descripcion: 'Calidad del producto, compatibilidad, defectos de fábrica, especificaciones.', orden: 20 },
  { codigo: 'comunicacion', nombre: 'Comunicación',      descripcion: 'Malentendidos, expectativas mal alineadas, demora en responder.',              orden: 30 },
  { codigo: 'financiero',   nombre: 'Financiero',        descripcion: 'Reembolsos, refunds, ajustes de precios, devoluciones de dinero.',             orden: 40 },
  { codigo: 'calidad',      nombre: 'Calidad operativa', descripcion: 'Fallas en procesos internos del seller (stock, atención, fulfillment).',       orden: 50 },
  { codigo: 'tecnico',      nombre: 'Técnico',           descripcion: 'Bugs, errores de plataforma, integración con ML/TN, sync fallando.',           orden: 60 },
]

// ───────────────────────────────────────────────────────────────
// Funciones de seed
// ───────────────────────────────────────────────────────────────

async function seedTiposCaso() {
  console.log('🌱 Seeding catálogo de tipos de caso...')
  let creados = 0
  let existentes = 0
  let errores = 0

  for (const tipo of CATALOGO_TIPOS) {
    try {
      const existente = await getTipoCasoPorCodigo(tipo.codigo)
      if (existente) {
        console.log(`  ⏭ ${tipo.codigo} ya existe (${existente.id})`)
        existentes++
        continue
      }
      const resultado = await upsertTipoCaso(null, tipo)
      console.log(`  ✅ ${tipo.codigo} → ${resultado.id}`)
      creados++
    } catch (e) {
      console.error(`  ❌ ${tipo.codigo}:`, e.message)
      errores++
    }
  }

  console.log(`\n🌱 Tipos de caso: ${creados} creados, ${existentes} ya existían, ${errores} errores`)
  return { creados, existentes, errores }
}

async function seedCategoriasCaso() {
  console.log('🌱 Seeding catálogo de categorías de caso...')
  let creados = 0
  let existentes = 0
  let errores = 0

  for (const cat of CATALOGO_CATEGORIAS) {
    try {
      // Buscar por codigo
      const q = query(collection(db, COLLECTIONS.CATEGORIAS_CASO), where('codigo', '==', cat.codigo))
      const snap = await getDocs(q)
      if (!snap.empty) {
        console.log(`  ⏭ ${cat.codigo} ya existe (${snap.docs[0].id})`)
        existentes++
        continue
      }
      const payload = forPlatformCreate({ ...cat, activo: true })
      const ref = await addDoc(collection(db, COLLECTIONS.CATEGORIAS_CASO), payload)
      console.log(`  ✅ ${cat.codigo} → ${ref.id}`)
      creados++
    } catch (e) {
      console.error(`  ❌ ${cat.codigo}:`, e.message)
      errores++
    }
  }

  console.log(`\n🌱 Categorías de caso: ${creados} creados, ${existentes} ya existían, ${errores} errores`)
  return { creados, existentes, errores }
}

// ───────────────────────────────────────────────────────────────
// Seedar SLAs canónicos de casos en la colección /slas
// ───────────────────────────────────────────────────────────────
// El motor `evaluarSLAsCore` (functions/slas.js, cron 5min) procesa slaInstancias
// que apuntan a un slaId del catálogo /slas. Para que los SLAs declarados
// en cada tipoCaso (slaDefaults) se enchufen al motor, generamos un slaDoc
// por cada combo (tipoCasoCodigo × slaTipo).
//
// Codificación: `caso-{tipoCasoCodigo}-{slaTipo}`
//   ej: 'caso-reclamo-ml-primera-respuesta', 'caso-reclamo-ml-resolucion'
//
// Estos slaDocs los consumen iniciarSLA() en casos.js al crear cada caso.

async function seedSLAsDeCasos() {
  console.log('🌱 Seeding SLAs canónicos de casos...')
  let creados = 0
  let existentes = 0
  let errores = 0

  for (const tipo of CATALOGO_TIPOS) {
    if (!tipo.slaDefaults?.length) continue
    for (const slaDef of tipo.slaDefaults) {
      const codigo = `caso-${tipo.codigo}-${slaDef.tipo}`
      try {
        // Buscar por código (idempotencia)
        const q = query(collection(db, COLLECTIONS.SLAS), where('codigo', '==', codigo))
        const snap = await getDocs(q)
        if (!snap.empty) {
          console.log(`  ⏭ SLA ${codigo} ya existe`)
          existentes++
          continue
        }
        await upsertSLA(null, {
          codigo,
          nombre: `${tipo.nombre} — ${slaDef.tipo.replace(/-/g, ' ')}`,
          descripcion: `SLA ${slaDef.tipo} para casos de tipo "${tipo.codigo}". Se aplica automáticamente al crear el caso.`,
          aplicaA: 'caso',  // Distingue de 'accionables' / 'mensajes'
          condiciones: { tipoCasoCodigo: tipo.codigo, slaTipo: slaDef.tipo },
          tiempoRespuestaMinutos: Number(slaDef.minutos),
          ventanaAlertaMinutos: Number(slaDef.ventanaAlertaMin) || 15,
          destinatariosAlertaExtra: [],
          activo: true,
          orden: tipo.orden * 10,
        })
        console.log(`  ✅ SLA ${codigo}`)
        creados++
      } catch (e) {
        console.error(`  ❌ SLA ${codigo}:`, e.message)
        errores++
      }
    }
  }

  console.log(`\n🌱 SLAs de casos: ${creados} creados, ${existentes} ya existían, ${errores} errores`)
  return { creados, existentes, errores }
}

// B8.1 — Registrar módulo 'casos' en modulos/ (idempotente)
async function registrarModuloCasos() {
  console.log('🌱 Registrando módulo Casos en modulos/...')
  const ref = doc(db, COLLECTIONS.MODULOS, 'casos')
  const snap = await getDoc(ref)
  if (snap.exists()) {
    console.log('  ⏭ modulos/casos ya existe')
    return { creado: false }
  }
  await setDoc(ref, {
    // ⚠️ `codigo` NO es decorativo y no se puede sacar (report uSLAndgaoonLDdolh6Aj).
    // `sincronizarModulosConFirestore` (`src/core/lib/modulos.js:1011`) arma
    // su índice de lo existente con `if (data.codigo) existentesPorCodigo.set(…)`:
    // un doc SIN el campo no entra al mapa, así que el sincronizador cree que
    // el módulo no existe y entra por la rama `!existente`, que hace **addDoc**
    // (`:1030`) — id aleatorio, sin pisar este doc. Resultado: DOS documentos
    // para Casos en `modulos`, el del seed (con `features`/`version`, sin
    // `funciones`, invisible para la matriz de /permisos) y el del catálogo.
    // Que el ID del doc sea 'casos' no alcanza: el sincronizador indexa por el
    // CAMPO, no por el id.
    codigo: 'casos',
    nombre: 'Casos',
    descripcion: 'Gestor de casos transversal — reclamos, devoluciones, auditorías, consultas. Caso es expediente investigativo con conversación, participantes internos+externos, vinculado a recursos y tareas.',
    tipo: 'core',
    version: '1.0.0',
    activo: true,
    modulosIds: [],  // empresas habilitadas (se llena vía /empresas/:id/modulos)
    // Sin campo `permisos`: era la TERCERA copia de la lista de permisos de
    // Casos —y la única que llegaba a persistirse en Firestore—, con los mismos
    // 13 códigos de la `CASOS_PERMISOS` que ya se retiró de `core/lib/casos.js`.
    // Nadie la lee (la matriz de `/permisos` lee `funciones` de
    // `MODULOS_CATALOGO`, `src/core/lib/modulos.js`), pero dejarla acá era
    // sembrar el drift de nuevo en cada entorno nuevo.
    features: ['casos', 'case-feed', 'multi-sla', 'portal-externo', 'casos-analytics', 'kanban', 'macros', 'plantillas', 'aprendizajes-kcs'],
    creadoEn: serverTimestamp(),
    actualizadoEn: serverTimestamp(),
  })
  console.log('  ✅ modulos/casos registrado')
  return { creado: true }
}

async function seedTiposYCategoriasCaso() {
  console.log('═══════════════════════════════════════════════════')
  console.log('  Seed de módulo Casos (tipos + categorías + SLAs + módulo)')
  console.log('═══════════════════════════════════════════════════')
  const tipos = await seedTiposCaso()
  console.log('')
  const categorias = await seedCategoriasCaso()
  console.log('')
  const slas = await seedSLAsDeCasos()
  console.log('')
  const modulo = await registrarModuloCasos()
  console.log('')
  console.log('═══════════════════════════════════════════════════')
  console.log(`  TOTAL: ${tipos.creados + categorias.creados + slas.creados + (modulo.creado ? 1 : 0)} creados, ${tipos.existentes + categorias.existentes + slas.existentes} ya existían`)
  console.log('═══════════════════════════════════════════════════')
  return { tipos, categorias, slas, modulo }
}

// ───────────────────────────────────────────────────────────────
// Exponer en window (admin runs desde consola)
// ───────────────────────────────────────────────────────────────
if (typeof window !== 'undefined') {
  window.__seedTiposCaso = seedTiposYCategoriasCaso        // default: todo (tipos + categorías + SLAs)
  window.__seedSoloTiposCaso = seedTiposCaso                // solo tipos
  window.__seedCategoriasCaso = seedCategoriasCaso          // solo categorías
  window.__seedSLAsDeCasos = seedSLAsDeCasos                // solo SLAs
}
